import { Button, Checkbox, InputNumber, Select, Table, Typography } from "antd";
import { observer } from "mobx-react-lite";
import React from "react";
import {
  computeFoodRowCost,
  computeFoodTotal,
} from "../calculations";
import type { FestivalCalcStore } from "../store";
import { AutocompleteInput } from "./shared/AutocompleteInput";
import { SectionWrapper } from "./shared/SectionWrapper";
import { TagPriceEditor } from "./shared/TagPriceEditor";
import { UserBadge } from "./shared/UserBadge";

interface Props {
  store: FestivalCalcStore;
}

export const FoodSection = observer(({ store }: Props) => {
  const { foodItems, tags, activeTagId, segments } = store;

  const allNames = foodItems.map((i) => i.name).filter(Boolean);
  const total = computeFoodTotal(foodItems, activeTagId, tags, segments);

  const segmentOptions = segments.map((s) => ({ value: s.id, label: `${s.name} (${s.attendeeCount})` }));

  const columns = [
    {
      title: "Aktiv",
      dataIndex: "active",
      width: 60,
      render: (_: any, item: (typeof foodItems)[0]) => (
        <Checkbox
          checked={item.active}
          onChange={(e) =>
            item.objectId && store.updateFoodItem(item.objectId, { active: e.target.checked })
          }
        />
      ),
    },
    {
      title: "Item",
      dataIndex: "name",
      render: (_: any, item: (typeof foodItems)[0]) => (
        <AutocompleteInput
          value={item.name}
          onChange={(v) => item.objectId && store.updateFoodItem(item.objectId, { name: v })}
          suggestions={allNames}
          placeholder="Item"
        />
      ),
    },
    {
      title: "Preis €/Person",
      dataIndex: "tagPrices",
      render: (_: any, item: (typeof foodItems)[0]) => {
        const { resolved } = computeFoodRowCost(item, activeTagId, tags, segments);
        return (
          <TagPriceEditor
            tagPrices={item.tagPrices}
            allTags={tags}
            priceField="pricePerPerson"
            resolved={resolved}
            onChange={(updated) =>
              item.objectId && store.updateFoodItem(item.objectId, { tagPrices: updated as any })
            }
          />
        );
      },
    },
    {
      title: "Tage",
      dataIndex: "eventSegmentIds",
      hidden: segmentOptions.length === 0,
      render: (_: any, item: (typeof foodItems)[0]) => (
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <Select
              mode="multiple"
              size="small"
            value={item.eventSegmentIds}
            disabled={!!item.persons}
              options={segmentOptions}
              onChange={(v) =>
                item.objectId && store.updateFoodItem(item.objectId, { eventSegmentIds: v })
              }
              placeholder="Tage"
              style={{ minWidth: 160 }}
            />
          
        </div>
      ),
    },
    {
      title: "Personen",
      render: (_: any, item: (typeof foodItems)[0]) => (
        <InputNumber
          size="small"
          min={0}
          step={1}
          value={item.eventSegmentIds.length > 0 ? segments.filter((s) => item.eventSegmentIds.includes(s.id)).reduce((sum, s) => sum + s.attendeeCount, 0) : item.persons}
          disabled={item.eventSegmentIds.length > 0}
          onChange={(v) =>
            item.objectId && store.updateFoodItem(item.objectId, { persons: v ?? 0 })
          }
          placeholder="Personen"
          style={{ width: 90 }}
        />
        ),
    },
    {
      title: "Gesamt",
      key: "total",
      render: (_: any, item: (typeof foodItems)[0]) => {
        const { cost } = computeFoodRowCost(item, activeTagId, tags, segments);
        return <Typography.Text>{cost.toFixed(2)} €</Typography.Text>;
      },
    },
    {
      title: "Ist-Kosten",
      key: "actualCost",
      width: 160,
      render: (_: any, item: (typeof foodItems)[0]) => {
        const { cost } = computeFoodRowCost(item, activeTagId, tags, segments);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Checkbox
              checked={item.isActual}
              onChange={(e) => {
                const patch: Partial<(typeof foodItems)[0]> = { isActual: e.target.checked };
                if (e.target.checked && item.actualCost === null) patch.actualCost = cost;
                item.objectId && store.updateFoodItem(item.objectId, patch);
              }}
            />
            {item.isActual && (
              <InputNumber
                size="small"
                min={0}
                step={0.01}
                value={item.actualCost ?? 0}
                onChange={(v) =>
                  item.objectId && store.updateFoodItem(item.objectId, { actualCost: v ?? 0 })
                }
                style={{ width: 95 }}
                addonAfter="€"
              />
            )}
          </div>
        );
      },
    },
    {
      title: "",
      dataIndex: "createdBy",
      width: 40,
      render: (_: any, item: (typeof foodItems)[0]) => <UserBadge user={item.createdBy} />,
    },
    {
      title: "",
      key: "delete",
      width: 40,
      render: (_: any, item: (typeof foodItems)[0]) => (
        <Button
          type="text"
          danger
          size="small"
          onClick={() => item.objectId && store.deleteFoodItem(item.objectId)}
        >
          ✖
        </Button>
      ),
    },
  ];

  return (
    <SectionWrapper title="Essen" total={total} onAdd={() => store.addFoodItem()}>
      <Table
        dataSource={foodItems}
        columns={columns}
        rowKey={(r) => r.objectId ?? Math.random().toString()}
        pagination={false}
        size="small"
      />
    </SectionWrapper>
  );
});
